'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The products hero artifact: one stage that switches between the three
 * product verbs, each with its own instrument drawing.
 *
 * Ported from `.verb-machine` in
 * public/pixelmojo-refactor/products.html (2026-08-08). Geometry, the 4.2s
 * cycle, and the layer cross-fade are the reference's; the drawings live in
 * globals.css under "Products verb machine".
 *
 * Interaction mirrors the reference: the stage auto-advances, and any tab
 * click both selects that product and restarts the timer. Under
 * prefers-reduced-motion nothing advances on its own, so no product is
 * reachable only by waiting.
 */
const PRODUCTS = [
  {
    key: 'radar',
    name: 'Radar',
    verb: 'sweeps',
    tagline: 'Sees what AI gets wrong.',
  },
  {
    key: 'vector',
    name: 'Vector',
    verb: 'points',
    tagline: 'Shows the move.',
  },
  {
    key: 'hive',
    name: 'Hive',
    verb: 'multiplies',
    tagline: 'The colony works as one.',
  },
] as const

const CYCLE_MS = 4200

export default function VerbMachine() {
  const [active, setActive] = useState(0)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)

  const startTimer = useCallback(() => {
    if (timer.current) clearInterval(timer.current)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    timer.current = setInterval(
      () => setActive(i => (i + 1) % PRODUCTS.length),
      CYCLE_MS
    )
  }, [])

  useEffect(() => {
    startTimer()
    return () => {
      if (timer.current) clearInterval(timer.current)
    }
  }, [startTimer])

  const select = (index: number) => {
    setActive(index)
    startTimer()
  }

  const product = PRODUCTS[active]

  return (
    <aside
      className='verb-machine'
      aria-label='Explore the Pixelmojo product family'
    >
      <div className='flex items-center justify-between gap-4 min-h-[48px] px-4 border-b border-line'>
        <span className='instrument-label text-muted-foreground'>
          The Pixelmojo product family
        </span>
        <span className='instrument-label text-accent-green'>
          Same grammar, different verb
        </span>
      </div>

      <div
        role='tablist'
        aria-label='Choose a product'
        className='grid grid-cols-3 border-b border-line'
      >
        {PRODUCTS.map((p, i) => (
          <button
            key={p.key}
            type='button'
            role='tab'
            aria-selected={i === active}
            aria-controls='verb-stage'
            id={`verb-tab-${p.key}`}
            onClick={() => select(i)}
            className={`instrument-label min-h-[54px] border-r border-line last:border-r-0 transition-colors ${
              i === active
                ? 'bg-[var(--cta-fill)] text-[var(--cta-foreground)]'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {p.verb}
          </button>
        ))}
      </div>

      <div
        className='verb-stage'
        id='verb-stage'
        data-product={product.key}
        role='tabpanel'
        aria-labelledby={`verb-tab-${product.key}`}
      >
        <span className='instrument-label absolute left-[25px] top-6 z-[5] text-muted-foreground'>
          {product.name}
        </span>
        <p className='thesis-headline thesis-headline--category absolute left-6 bottom-[18px] z-[5] leading-none'>
          {product.verb}
        </p>

        <div className='visual-layer visual-radar' aria-hidden='true'>
          <div className='radar-rings' />
          <div className='radar-cross' />
          <div className='sweep-beam' />
        </div>

        <div className='visual-layer visual-vector' aria-hidden='true'>
          <div className='vector-axis' />
          <i className='vector-ray one' />
          <i className='vector-ray two' />
          <i className='vector-ray three' />
          <i className='vector-ray four' />
          <div className='vector-node' />
        </div>

        <div className='visual-layer visual-hive' aria-hidden='true'>
          <div className='hive-cells'>
            {Array.from({ length: 25 }, (_, i) => (
              <i key={i} />
            ))}
          </div>
        </div>
      </div>

      <div className='grid grid-cols-[110px_1fr] items-center gap-[18px] min-h-[76px] px-[17px] py-3.5 border-t border-line'>
        <span className='instrument-label text-cta-ink'>{product.name}</span>
        <p className='card-headline card-headline--base text-foreground-soft'>
          {product.tagline}
        </p>
      </div>
    </aside>
  )
}
